/**
 * StepMetadata - Step 3: Basic Metadata Entry
 *
 * Allows users to:
 * - Add a caption/title
 * - Add a description
 * - Add an estimated date (flexible text)
 * - Add a location (text or structured)
 * - Link to a memory
 * - Tag people mentioned in the snippet
 */

import { useState, useEffect } from "react";
import { SnippetItem, SnippetDraftData } from "../../../api/snippets";
import { useMediaUrl } from "../../../hooks/useMediaUrl";
import { LocationPicker } from "../../Location";
import { MemoryPicker } from "../../Memory";
import { PersonPicker } from "../../Person";

interface StepMetadataProps {
  snippet: SnippetItem;
  draftData: SnippetDraftData;
  updateDraft: (updates: Partial<SnippetDraftData>) => void;
  imageKey: number;
}

export function StepMetadata({
  snippet,
  draftData,
  updateDraft,
  imageKey,
}: StepMetadataProps) {
  // Image preview
  const { url: imageUrl, isLoading: imageLoading } = useMediaUrl(
    snippet.id,
    snippet.file_size + imageKey
  );

  // Local state for form
  const [caption, setCaption] = useState(draftData.caption || "");
  const [description, setDescription] = useState(draftData.description || "");
  const [estimatedDateText, setEstimatedDateText] = useState(draftData.estimated_date_text || "");
  const [location, setLocation] = useState(draftData.location || "");
  const [locationId, setLocationId] = useState<number | null>(draftData.location_id || null);
  const [memoryId, setMemoryId] = useState<number | null>(draftData.memory_id || null);
  const [personIds, setPersonIds] = useState<number[]>(draftData.person_ids || []);
  const [connectedPersonIds, setConnectedPersonIds] = useState<number[]>(draftData.connected_person_ids || []);

  // Update draft when values change (debounced via the auto-save hook)
  useEffect(() => {
    updateDraft({
      caption: caption || null,
      description: description || null,
      estimated_date_text: estimatedDateText || null,
      location: location || null,
      location_id: locationId,
      memory_id: memoryId,
      person_ids: personIds,
      connected_person_ids: connectedPersonIds,
    });
  }, [caption, description, estimatedDateText, location, locationId, memoryId, personIds, connectedPersonIds, updateDraft]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Image preview */}
      <div className="bg-vault-100 rounded-lg p-4 min-h-[300px] flex items-center justify-center">
        {imageLoading ? (
          <div className="flex flex-col items-center gap-2 text-vault-500">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Loading image...</span>
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={snippet.original_filename}
            className="max-w-full max-h-[400px] object-contain rounded shadow-lg"
          />
        ) : (
          <div className="text-vault-400">No image available</div>
        )}
      </div>

      {/* Metadata form */}
      <div className="space-y-5">
        {/* Caption */}
        <div>
          <label htmlFor="caption" className="block text-sm font-semibold text-vault-800 mb-2">
            Title / Caption
          </label>
          <input
            id="caption"
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g., Baptism record of John Smith"
            maxLength={500}
            className="w-full px-4 py-2 rounded-lg border border-vault-300 bg-white text-vault-900 placeholder-vault-400 focus:border-vault-500 focus:ring-1 focus:ring-vault-500"
          />
          <p className="text-xs text-vault-500 mt-1">
            A short title to identify this snippet ({caption.length}/500)
          </p>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-semibold text-vault-800 mb-2">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add any notes or context about this record..."
            rows={4}
            className="w-full px-4 py-2 rounded-lg border border-vault-300 bg-white text-vault-900 placeholder-vault-400 focus:border-vault-500 focus:ring-1 focus:ring-vault-500 resize-none"
          />
          <p className="text-xs text-vault-500 mt-1">
            Additional details, notes, or context
          </p>
        </div>

        {/* Estimated Date */}
        <div>
          <label htmlFor="estimatedDate" className="block text-sm font-semibold text-vault-800 mb-2">
            Date (when was this created?)
          </label>
          <input
            id="estimatedDate"
            type="text"
            value={estimatedDateText}
            onChange={(e) => setEstimatedDateText(e.target.value)}
            placeholder="e.g., 15 March 1842, circa 1890, 1850s"
            className="w-full px-4 py-2 rounded-lg border border-vault-300 bg-white text-vault-900 placeholder-vault-400 focus:border-vault-500 focus:ring-1 focus:ring-vault-500"
          />
          <p className="text-xs text-vault-500 mt-1">
            Enter the date flexibly - exact dates, circa dates, or date ranges all work
          </p>
        </div>

        {/* Location Text */}
        <div>
          <label htmlFor="location" className="block text-sm font-semibold text-vault-800 mb-2">
            Location (text)
          </label>
          <input
            id="location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., St Mary's Church, Whitechapel, London"
            className="w-full px-4 py-2 rounded-lg border border-vault-300 bg-white text-vault-900 placeholder-vault-400 focus:border-vault-500 focus:ring-1 focus:ring-vault-500"
          />
          <p className="text-xs text-vault-500 mt-1">
            Free text description of the location
          </p>
        </div>

        {/* Location Picker */}
        <div>
          <label className="block text-sm font-semibold text-vault-800 mb-2">
            Link to Location
          </label>
          <LocationPicker
            value={locationId}
            onChange={setLocationId}
            placeholder="Link to a specific location..."
          />
          <p className="text-xs text-vault-500 mt-1">
            Link to a reusable location for better organization
          </p>
        </div>

        {/* Memory Picker */}
        <div>
          <label className="block text-sm font-semibold text-vault-800 mb-2">
            Link to Memory
          </label>
          <MemoryPicker
            value={memoryId}
            onChange={setMemoryId}
            placeholder="Add to a memory..."
          />
          <p className="text-xs text-vault-500 mt-1">
            Group this snippet with related photos and people
          </p>
        </div>

        {/* Person Picker */}
        <div>
          <label className="block text-sm font-semibold text-vault-800 mb-2">
            Tag People
          </label>
          <PersonPicker
            selectedPersonIds={personIds}
            selectedConnectedPersonIds={connectedPersonIds}
            onAddPerson={(id) => setPersonIds([...personIds, id])}
            onAddConnectedPerson={(id) => setConnectedPersonIds([...connectedPersonIds, id])}
            onRemovePerson={(id) => setPersonIds(personIds.filter((p) => p !== id))}
            onRemoveConnectedPerson={(id) => setConnectedPersonIds(connectedPersonIds.filter((p) => p !== id))}
            placeholder="Tag people mentioned in this record..."
          />
          <p className="text-xs text-vault-500 mt-1">
            Tag family members or connected persons mentioned in this snippet
          </p>
        </div>

        {/* Info box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-medium text-amber-800 text-sm mb-1">
            All fields are optional
          </h4>
          <p className="text-amber-700 text-sm">
            You can add or edit this information later from the Media Library.
            Adding details now helps with search and organization.
          </p>
        </div>
      </div>
    </div>
  );
}

export default StepMetadata;
